import { createSelector } from "@reduxjs/toolkit";
import { OPERATION_MODE } from "../varibles/constants";
import { getMultiSetting, getSetting } from "../varibles/defaultSetting";
import { PreferenceSetting } from "../varibles/userSetting";
import { AppState } from "./store";

type SliceSelectors<Name extends string, Slice> = {
    root: (state: Record<Name, Slice>) => Slice;
} & {
    [Key in keyof Slice]-?: (state: Record<Name, Slice>) => Slice[Key];
};

export const createSliceSelectors = <Name extends string, Slice extends object>(
    name: Name,
    initialState: Slice,
): SliceSelectors<Name, Slice> => {
    const root = (state: Record<Name, Slice>) => state[name];
    const selectors = Object.fromEntries(
        (Object.keys(initialState) as Array<keyof Slice>).map(key => [
            key,
            (state: Record<Name, Slice>) => root(state)[key],
        ]),
    );

    return { ...selectors, root } as SliceSelectors<Name, Slice>;
};

export const createSliceSelector = <Slice, RootState = AppState>(root: (state: RootState) => Slice) => {
    const selector = <T>(fn: (slice: Slice) => T) =>
        (state: RootState) => fn(root(state));

    return { root, selector, };
};

// Login
export const selectSetting = (keys: string[]) => createSelector(
    (state: AppState) => state.login.user.setting,
    data => getMultiSetting(data, keys),
    {
        memoizeOptions: {
            resultEqualityCheck: (previous: Record<string, unknown>, current: Record<string, unknown>) =>
                keys.every(key => Object.is(previous[key], current[key])),
        },
    }
);
export const selectOneSetting = (key: string) => createSelector(
    (state: AppState) => state.login.user.setting,
    data => getSetting(data, key),
    {
        memoizeOptions: {
            resultEqualityCheck: Object.is,
        },
    }
);

export const selectUserId = (state: AppState) => state.login.user.user_id || '';
export const selectDefaultOpType = (state: AppState) => state.login.user.tenant.default_op_type;
export const selectUser = (state: AppState) => state.login.user || {};
export const selectLoadingUser = (state: AppState) => state.login.loadingUser;
export const selectUsername = (state: AppState) => state.login.user.username;
export const selectRoles = (state: AppState) => state.login.user.roles || [];
export const selectPermission = (state: AppState) => state.login.user.permission || {};
export const selectHasPermissions = createSelector(
    selectPermission,
    permission => Object.keys(permission).length > 0
);
export const selectPermissions = (keys: string[]) => createSelector(
    selectPermission,
    permission => keys.map(item => Boolean(permission[item])),
    {
        memoizeOptions: {
            resultEqualityCheck: (previous: boolean[], current: boolean[]) =>
                previous.length === current.length && previous.every((value, index) => value === current[index]),
        },
    }
);
export const selectPagePermissions = (page: { [actionName: string]: string }) => createSelector(
    selectPermission,
    permission => Object.fromEntries(
        Object.values(page).map((action) => [
            action.slice(action.lastIndexOf("/") + 1),
            !!permission?.[action],
        ])
    ),
    {
        memoizeOptions: {
            resultEqualityCheck: (previous: Record<string, boolean>, current: Record<string, boolean>) => {
                const keys = Object.keys(previous);
                return keys.length === Object.keys(current).length
                    && keys.every(key => previous[key] === current[key]);
            },
        },
    }
);
export const selectTenantId = (state: AppState) => state.login.user.tenant_id;
export const selectDefaultVessel = (state: AppState) => state.login.user.default_vessel;
export const selectTenant = (state: AppState) => state.login.user.tenant;
export const selectOpMode = (state: AppState) => state.login.user.tenant.operation_mode || OPERATION_MODE.PLAN;
export const selectVesselsSide = (state: AppState) => state.login.vessels || [];
export const selectIsDashboardEnabled = (state: AppState) => state.login.isDashboardEnabled;
export const selectViewMode = (state: AppState) => getSetting(state.login.user.setting, PreferenceSetting.VIEW_MODE)

// Dashboard
export const selectDashboard = {
    prodAreas: (state: AppState) => state.dashboard.productionAreas,
    loadingProdAreas: (state: AppState) => state.dashboard.loadingProdAreas,
    loadingOp: (state: AppState) => state.dashboard.loadingOp,
    vessels: (state: AppState) => state.dashboard.vessels,
    loadingVessels: (state: AppState) => state.dashboard.loadingVessels,
    operations: (state: AppState) => state.dashboard.operations,
    opsSite: (state: AppState) => state.dashboard.opsSite,
    target: (state: AppState) => state.dashboard.target,
    tracking: (state: AppState) => state.dashboard.tracking,
}

// Overview
export const selectOverview = {
    root: (state: AppState) => state.overview,
    weeks: (state: AppState) => state.overview.weeks,
    ops: (state: AppState) => state.overview.operations,
    level: (state: AppState) => state.overview.level,
    vessels: (state: AppState) => state.overview.vessels,
    wellboats: (state: AppState) => state.overview.wellboats,
    loadingData: (state: AppState) => state.overview.loadingData,
    loadingLazy: (state: AppState) => state.overview.loadingLazy,
    beforeCount: (state: AppState) => state.overview.beforeCount,
    afterCount: (state: AppState) => state.overview.afterCount,
    showAllPlans: (state: AppState) => state.overview.showAllPlans,
    sites: (state: AppState) => state.overview.sites,
    opsSite: (state: AppState) => state.overview.opsSite,
}

// Configuration
export const selectConfiguration = {
    iotName: (state: AppState) => state.configuration.iotName
}

// Route
export const selectRoute = {
    mode: (state: AppState) => state.route.mode,
    loadingRoute: (state: AppState) => state.route.loadingAuto,
    params: (state: AppState) => state.route.params,
    loadingRoutes: (state: AppState) => state.route.loading.routes,
    loadingLocations: (state: AppState) => state.route.loadingLocations,
    locations: (state: AppState) => state.route.locations,
}

// Message
export const selectMessage = {
    messages: (state: AppState) => state.message.messages || [],
    conversations: (state: AppState) => state.message.conversations || [],
    chatIndex: (state: AppState) => state.message.chatIndex,
    operationsIndex: (state: AppState) => state.message.operationsIndex,
    newConversationType: (state: AppState) => state.message.newConversationType
}

// Vessel detail
export const selectVessel = {
    replay: (state: AppState) => state.vesselDetail.replay,
    dashboard: (state: AppState) => state.vesselDetail.dashboard,
    isLive: (state: AppState) => state.vesselDetail.isLive,
    isTracking: (state: AppState) => state.vesselDetail.isTracking,
    isDragging: (state: AppState) => state.vesselDetail.isDragging,
    vessel: (state: AppState) => state.vesselDetail.vessel,
    history: (state: AppState) => state.vesselDetail.history,
    currentTime: (state: AppState) => state.vesselDetail.currentTime,
    finishTime: (state: AppState) => state.vesselDetail.history.finishTime,
    vesselLive: (state: AppState) => state.vesselDetail.vesselLive,
    iot_name: (state: AppState) => state.vesselDetail.vessel?.iot_name,
    deviceIds: (state: AppState) => state.vesselDetail.deviceIds,
    update: (state: AppState) => state.vesselDetail.update,
}

// Operation detail
export const selectOpDetail = {
    data: (state: AppState) => state.operationDetail.data,
    activityLog: (state: AppState) => state.operationDetail.data?.activity_log,
    loading: (state: AppState) => state.operationDetail.loading,
}

// Weather
export const selectWeather = {
    locations: (state: AppState) => state.weather.locations
}
